import { Alert, Button, Card, Flex, Space, Typography, Upload, notification } from 'antd'
import { useState } from 'react'
import { faCloudArrowUp, faPaperclip, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uploadProposalAttachment } from './proposalApi'
import AttachExistingFilesModal from './AttachExistingFilesModal'
import {
  appendAttachment,
  attachmentErrors,
  attachmentDisplayName,
  attachmentSubtitle,
  canAcceptMore,
  maxAttachmentCount,
  ProposalAttachment,
  rejectionReason,
  removeAttachment
} from './proposalAttachmentsModel'
import './ProposalAttachments.css'

type Props = {
  attachments: ProposalAttachment[]
  customerId: number
  jobId: number
  onChange: (attachments: ProposalAttachment[]) => void
  onAdd: (attachment: ProposalAttachment) => void
  embedded?: boolean
}

const ProposalAttachments = ({ attachments, customerId, jobId, onChange, onAdd , embedded = false}: Props) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const errors = attachmentErrors(attachments)

  const upload = async (file: File) => {
    if (!canAcceptMore(attachments, 1)) {
      notification.warning({ message: `You can attach at most ${maxAttachmentCount} files to a proposal.` })
      return
    }

    const rejection = rejectionReason(file)
    if (rejection) {
      notification.warning({ message: rejection })
      return
    }

    setIsUploading(true)
    try {
      const uploaded = await uploadProposalAttachment(file, customerId, jobId)
      onAdd(uploaded)
    } catch {
      notification.error({ message: `${file.name} could not be uploaded.` })
    } finally {
      setIsUploading(false)
    }
  }

  const uploadProps = {
    multiple: true,
    showUploadList: false,
    beforeUpload: (file: File) => {
      upload(file)
      return false
    }
  }

  const heading = <Space>
    Attachments
    <span className="proposal-attachments__count">{attachments.length}</span>
  </Space>

  const headerActions = <Button icon={<FontAwesomeIcon icon={faPaperclip} />} onClick={() => setIsPickerOpen(true)}>
    Attach Existing
  </Button>

  // The drop zone renders whether or not files are already attached. It used to be
  // swapped out for the list, which was fine while the header carried its own Upload
  // button — with that removed (SYN-3821) hiding the zone would leave no way to upload
  // a second file.
  const dropZone = <Upload.Dragger {...uploadProps} showUploadList={false} className="proposal-attachments__dragger">
    <div className="proposal-attachments__dropzone">
      <FontAwesomeIcon icon={faCloudArrowUp} className="proposal-attachments__icon" />
      <div className="proposal-attachments__prompt">
        {isUploading
          ? 'Uploading…'
          : <>Drag &amp; drop files here or <Typography.Link>Select Files</Typography.Link></>}
      </div>
    </div>
  </Upload.Dragger>

  const body = <Flex vertical gap="small">
      {errors.map(x => <Alert key={x} type="warning" showIcon message={x} />)}

      {attachments.map(attachment => <div key={attachment.attachmentId} className="proposal-attachments__row">
        <div>
          <div className="proposal-attachments__name">{attachmentDisplayName(attachment)}</div>
          <div className="proposal-attachments__meta">{attachmentSubtitle(attachment)}</div>
        </div>
        <Button
          type="text"
          danger
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => onChange(removeAttachment(attachments, attachment.attachmentId))}
        />
      </div>)}

      {dropZone}
    </Flex>

  const picker = <AttachExistingFilesModal
    open={isPickerOpen}
    customerId={customerId}
    alreadyAttached={attachments}
    onClose={() => setIsPickerOpen(false)}
    onAttach={added => onChange(added.reduce(appendAttachment, attachments))}
  />

  if (embedded) {
    return <>
      <Flex align="center" justify="space-between" style={{ marginBottom: '8px' }}>
        {heading}
        {headerActions}
      </Flex>
      {body}
      {picker}
    </>
  }

  return <Card title={heading} size="small" extra={headerActions}>
    {body}
    {picker}
  </Card>
}

export default ProposalAttachments
